/**
 * hasBusinessContext — Phase 4.4c-3.
 *
 * Single source of truth for "is this user's business state ready to
 * enter the tenant app?" A user is tenant-ready iff BOTH:
 *
 *   - `hasRegisteredBusiness === true`, AND
 *   - `businessId` is a non-empty (after-trim) string.
 *
 * Truthiness alone isn't enough. The `apiClient` request interceptor
 * derives the `x-business-id` header from `user.businessId`; an
 * inconsistent shape like `{ hasRegisteredBusiness: true,
 * businessId: null }` would let a user past the guard and into the
 * tenant shell, where every API call would land without
 * `x-business-id` and 403. Whitespace-only ids leak the same way:
 * the interceptor truthy-checks the value before attaching, so
 * `'   '` would technically pass through as a header but the backend
 * would reject it.
 *
 * Living in `core/auth` (not `app/`) so feature code (e.g.
 * `use-login.ts`) can import the predicate without inverting the
 * dependency direction (feature → app composition).
 */

import type { User } from '@/state/stores/auth.store';

export function hasBusinessContext(user: User | null | undefined): boolean {
  if (!user) return false;
  if (!user.hasRegisteredBusiness) return false;
  if (typeof user.businessId !== 'string') return false;
  return user.businessId.trim().length > 0;
}
